import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import InfoCategoryArc from "./components/InfoCategoryArc";
import VitalsCategoryArc from "./components/VitalsCategoryArc";
import LifestyleCategoryArc from "./components/LifestyleCategoryArc";
import HealthCategoryArc from "./components/HealthCategoryArc";
import MentalHealthCategoryArc from "./components/MentalHealthCategoryArc";
import HealthCategoryIcon from "./components/HealthCategoryIcon";
import InfoCategoryIcon from "./components/InfoCategoryIcon";
import LifestyleCategoryIcon from "./components/LifestyleCategoryIcon";
import MentalHealthCategoryIcon from "./components/MentalHealthCategoryIcon";
import VitalsCategoryIcon from "./components/VitalsCategoryIcon";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  WellnessWheelCategory,
  WellnessWheelProps,
  WellnessWheelScore,
} from "./types";
import { useScoreAnimation } from "./hooks/useScoreAnimation";
import "./animation.css";
import { useTranslation } from "react-i18next";

const SCORE_COLOR_MAP = {
  disabled: {
    iconColorFill: theme.palette.neutral.light,
    arcColorFill: theme.palette.neutral[300],
  },
  low: {
    iconColorFill: theme.palette.accent4.main,
    arcColorFill: theme.palette.accent4.light,
  },
  medium: {
    iconColorFill: theme.palette.secondary.main,
    arcColorFill: theme.palette.secondary.light,
  },
  high: {
    iconColorFill: theme.palette.accent1.main,
    arcColorFill: theme.palette.accent1.light,
  },
};

function getScoreColor(score: number): string {
  switch (true) {
    case score < WellnessWheelScore.low:
      return SCORE_COLOR_MAP.low.iconColorFill;
    case score <= WellnessWheelScore.medium:
      return SCORE_COLOR_MAP.medium.iconColorFill;
    default:
      return SCORE_COLOR_MAP.high.iconColorFill;
  }
}

function getCategoryColour(category: WellnessWheelCategory) {
  const score = category.categoryScore || 0;

  if (!category.enabled || category.categoryScore === 0) {
    return {
      iconColorFill: SCORE_COLOR_MAP.disabled.iconColorFill,
      arcColorFill: SCORE_COLOR_MAP.disabled.arcColorFill,
    };
  }

  switch (true) {
    case score < WellnessWheelScore.low:
      return SCORE_COLOR_MAP.low;
    case score <= WellnessWheelScore.medium:
      return SCORE_COLOR_MAP.medium;
    default:
      return SCORE_COLOR_MAP.high;
  }
}

export function WellnessWheel({
  score,
  maxScore = 100,
  scoreColorOverride,
  info = { enabled: true, categoryScore: 0 },
  mentalHealth = { enabled: true, categoryScore: 0 },
  health = { enabled: true, categoryScore: 0 },
  lifestyle = { enabled: true, categoryScore: 0 },
  vitals = { enabled: true, categoryScore: 0 },
}: WellnessWheelProps) {
  const { t } = useTranslation();
  const { displayScore, isVisible } = useScoreAnimation(score);

  const scoreText = displayScore.toString();
  const scoreColor = scoreColorOverride ?? getScoreColor(displayScore);

  const categories = {
    health: getCategoryColour(health),
    mentalHealth: getCategoryColour(mentalHealth),
    lifestyle: getCategoryColour(lifestyle),
    vitals: getCategoryColour(vitals),
    info: getCategoryColour(info),
  };

  const categoryComponents = {
    health: { Arc: HealthCategoryArc, Icon: HealthCategoryIcon },
    mentalHealth: {
      Arc: MentalHealthCategoryArc,
      Icon: MentalHealthCategoryIcon,
    },
    lifestyle: { Arc: LifestyleCategoryArc, Icon: LifestyleCategoryIcon },
    vitals: { Arc: VitalsCategoryArc, Icon: VitalsCategoryIcon },
    info: { Arc: InfoCategoryArc, Icon: InfoCategoryIcon },
  };

  return (
    <Stack gap={2}>
      <Typography variant="h2">{t("WellnessWheel.title")}</Typography>
      <Typography variant="body1">{t("WellnessWheel.description")}</Typography>
      <Box
        sx={{
          width: "100%",
          height: 298,
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 275 280"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="My Wellness Wheel"
        >
          {Object.entries(categoryComponents).map(([key, { Arc }]) => (
            <Arc key={key} {...categories[key as keyof typeof categories]} />
          ))}

          {Object.entries(categoryComponents).map(([key, { Icon }]) => (
            <Icon key={key} {...categories[key as keyof typeof categories]} />
          ))}
        </svg>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -65%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: convertPxToRem(64),
              lineHeight: 1,
              color: scoreColor,
              opacity: isVisible ? 1 : 0,
            }}
          >
            {scoreText}
          </Typography>
        </Box>

        <Typography
          sx={{
            position: "absolute",
            top: "55%",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          out of {maxScore}
        </Typography>
      </Box>
      <Typography variant="h2">
        {t("WellnessWheel.contribution.title")}
      </Typography>
      <Typography variant="body1">
        {t("WellnessWheel.contribution.description")}
      </Typography>
    </Stack>
  );
}

export default WellnessWheel;
